// /services/strategic-planning: the flagship BR-corporate offer, structured
// exactly like the proposals that already sell it. Three phases, artifacts,
// an annual cadence, and Hoshin Kanri named as the method.

const strategicPlanningContent = {
  'en-US': {
    seo: {
      title: 'Strategic Planning Facilitation | Lean it 101',
      description:
        'Hoshin Kanri applied for real: an executive workshop, structured catchball rounds, and a 12-month execution cadence that keeps the strategy alive.',
    },
    hero: {
      kicker: 'Strategy',
      headline: 'Strategy your company actually executes.',
      body: 'Most annual plans die in the slide deck. This engagement runs on Hoshin Kanri: objectives become indicators, indicators become initiatives with owners, and a monthly rhythm confronts the plan with the numbers all year.',
      method: 'Based on Hoshin Kanri, run by the practice behind the Lean Pyramid.',
    },
    phases: {
      kicker: 'The engagement, in three phases',
      note: 'Each phase can be contracted separately.',
      items: [
        {
          n: '1',
          name: 'Executive Workshop',
          duration: '2 days plus preparation',
          detail: 'Strategic direction, priority objectives, learnings from the current year and preliminary key results, with the whole leadership in the room.',
        },
        {
          n: '2',
          name: 'Deployment and Catchball',
          duration: 'about 8 weeks',
          detail: 'The objectives travel through the organization in structured rounds: proposals go down, capacity and commitments come back, twice, until the plan is one the areas actually signed.',
        },
        {
          n: '3',
          name: 'Execution Mentoring',
          duration: '12 months',
          detail: 'A monthly strategy review on real indicators, mentoring hours for the owners, and a sponsor check-in cadence, so the plan adapts while it still can.',
        },
      ],
    },
    artifacts: {
      kicker: 'What the work produces',
      items: [
        { name: 'Strategic Map / X-Matrix', detail: 'Objectives, key results, initiatives and owners in one integrated view.' },
        { name: 'Strategy Scorecard', detail: 'Baseline, target, current and forecast per indicator.' },
        { name: 'Management Dashboard', detail: 'The scorecard live in your own tools, fed by your own data.' },
        { name: 'Strategic Playbook', detail: 'Strategy, agreements, indicators and the management routine, written down.' },
      ],
    },
    cadence: {
      kicker: 'The annual rhythm',
      title: 'The strategy stays alive for twelve months',
      detail: 'Monthly reviews on results and forecasts, decisions surfaced early, and the next annual cycle starting from data rather than from memory.',
    },
    cta: { label: 'Open a card about strategic planning', sub: 'Answered by the facilitator, within one business day.' },
  },

  'pt-BR': {
    seo: {
      title: 'Facilitação de Planejamento Estratégico | Lean it 101',
      description:
        'Hoshin Kanri aplicado de verdade: workshop executivo, rodadas estruturadas de catchball, e uma cadência de execução de 12 meses que mantém a estratégia viva.',
    },
    hero: {
      kicker: 'Estratégia',
      headline: 'Estratégia que a sua empresa executa de verdade.',
      body: 'A maioria dos planejamentos anuais morre no slide. Este engajamento roda sobre Hoshin Kanri: objetivos viram indicadores, indicadores viram iniciativas com donos, e um ritmo mensal confronta o plano com os números o ano inteiro.',
      method: 'Baseado em Hoshin Kanri, conduzido pela prática por trás da Pirâmide Lean.',
    },
    phases: {
      kicker: 'O engajamento, em três fases',
      note: 'Cada fase pode ser contratada separadamente.',
      items: [
        {
          n: '1',
          name: 'Workshop Executivo',
          duration: '2 dias mais preparação',
          detail: 'Direção estratégica, objetivos prioritários, aprendizados do ano corrente e key results preliminares, com a liderança inteira na sala.',
        },
        {
          n: '2',
          name: 'Desdobramento e Catchball',
          duration: 'cerca de 8 semanas',
          detail: 'Os objetivos percorrem a organização em rodadas estruturadas: propostas descem, capacidade e compromissos sobem, duas vezes, até o plano ser um que as áreas de fato assinaram.',
        },
        {
          n: '3',
          name: 'Mentoria de Execução',
          duration: '12 meses',
          detail: 'Review mensal de estratégia sobre indicadores reais, horas de mentoria para os responsáveis, e cadência de check-in com o patrocinador, para o plano se adaptar enquanto ainda pode.',
        },
      ],
    },
    artifacts: {
      kicker: 'O que o trabalho produz',
      items: [
        { name: 'Mapa Estratégico / X-Matrix', detail: 'Objetivos, key results, iniciativas e responsáveis em uma visão integrada.' },
        { name: 'Strategy Scorecard', detail: 'Baseline, meta, atual e forecast por indicador.' },
        { name: 'Dashboard de Gestão', detail: 'O scorecard vivo nas suas próprias ferramentas, alimentado pelos seus dados.' },
        { name: 'Playbook Estratégico', detail: 'Estratégia, acordos, indicadores e a rotina de gestão, por escrito.' },
      ],
    },
    cadence: {
      kicker: 'O ritmo anual',
      title: 'A estratégia permanece viva por doze meses',
      detail: 'Reviews mensais sobre resultados e forecasts, decisões antecipadas, e o próximo ciclo anual começando de dados em vez de memória.',
    },
    cta: { label: 'Abrir um cartão sobre planejamento estratégico', sub: 'Respondido pelo facilitador, em até um dia útil.' },
  },

  es: {
    seo: {
      title: 'Facilitación de Planificación Estratégica | Lean it 101',
      description:
        'Hoshin Kanri aplicado en serio: workshop ejecutivo, rondas estructuradas de catchball, y una cadencia de ejecución de 12 meses que mantiene viva la estrategia.',
    },
    hero: {
      kicker: 'Estrategia',
      headline: 'Estrategia que tu empresa ejecuta de verdad.',
      body: 'La mayoría de los planes anuales muere en la presentación. Este compromiso corre sobre Hoshin Kanri: los objetivos se vuelven indicadores, los indicadores iniciativas con dueños, y un ritmo mensual confronta el plan con los números todo el año.',
      method: 'Basado en Hoshin Kanri, conducido por la práctica detrás de la Pirámide Lean.',
    },
    phases: {
      kicker: 'El compromiso, en tres fases',
      note: 'Cada fase puede contratarse por separado.',
      items: [
        {
          n: '1',
          name: 'Workshop Ejecutivo',
          duration: '2 días más preparación',
          detail: 'Dirección estratégica, objetivos prioritarios, aprendizajes del año en curso y key results preliminares, con todo el liderazgo en la sala.',
        },
        {
          n: '2',
          name: 'Despliegue y Catchball',
          duration: 'unas 8 semanas',
          detail: 'Los objetivos recorren la organización en rondas estructuradas: las propuestas bajan, la capacidad y los compromisos suben, dos veces, hasta que el plan es uno que las áreas realmente firmaron.',
        },
        {
          n: '3',
          name: 'Mentoría de Ejecución',
          duration: '12 meses',
          detail: 'Review mensual de estrategia sobre indicadores reales, horas de mentoría para los responsables, y cadencia de check-in con el patrocinador, para que el plan se adapte mientras aún puede.',
        },
      ],
    },
    artifacts: {
      kicker: 'Lo que el trabajo produce',
      items: [
        { name: 'Mapa Estratégico / X-Matrix', detail: 'Objetivos, key results, iniciativas y responsables en una vista integrada.' },
        { name: 'Strategy Scorecard', detail: 'Baseline, meta, actual y forecast por indicador.' },
        { name: 'Dashboard de Gestión', detail: 'El scorecard vivo en tus propias herramientas, alimentado por tus datos.' },
        { name: 'Playbook Estratégico', detail: 'Estrategia, acuerdos, indicadores y la rutina de gestión, por escrito.' },
      ],
    },
    cadence: {
      kicker: 'El ritmo anual',
      title: 'La estrategia sigue viva durante doce meses',
      detail: 'Reviews mensuales sobre resultados y forecasts, decisiones anticipadas, y el próximo ciclo anual empezando desde datos en lugar de memoria.',
    },
    cta: { label: 'Abrir una tarjeta sobre planificación estratégica', sub: 'Respondida por el facilitador, dentro de un día hábil.' },
  },
}

const normalize = (language) => {
  if (!language) return 'en-US'
  const lower = language.toLowerCase()
  if (lower.startsWith('pt')) return 'pt-BR'
  if (lower.startsWith('es')) return 'es'
  return 'en-US'
}

export const getStrategicPlanningContent = (language) =>
  strategicPlanningContent[normalize(language)]
export default strategicPlanningContent
